import { create } from 'zustand'
import { isPassiveJoinUrl } from '@/features/meetings/utils/joinLinks'
import {
  ensureDevicePermission,
  notifyDeviceBlocked,
} from '@/features/devices/services/permission-gate'
import { useDevicesStore } from '@/features/devices/store/devices.store'
import type { MediaKind } from '@/features/devices/services/device-manager'
import { unmuteRemoteVoicesNow } from '../services/audio-autoplay'
import type { ParticipantRole } from "@vc/models"

/**
 * Turning a device ON is a request, not a decision.
 *
 * Turning one off always works, so it stays a plain flip. Turning one on has to
 * clear the browser first, and doing that HERE — rather than in the control-bar
 * button — is what covers the keyboard shortcut, push-to-talk, the host's
 * "please unmute" prompt and the mobile bar as well. The same reasoning as the
 * `viewOnly` guard below: these flags are reachable from too many places for a
 * check in one of them to hold.
 *
 * Nothing flips until the grant lands, so a refused camera stays visibly off
 * instead of lighting a button with no capture behind it.
 */
async function whenAllowed(kind: MediaKind, turnOn: () => void): Promise<void> {
  const outcome = await ensureDevicePermission(kind)
  if (outcome === 'granted') {
    turnOn()
    return
  }
  // Blocked in the browser. Instead of the old corner toast ("Microphone is
  // blocked, allow it from the address bar"), raise the centered recovery
  // dialog, which explains why and can re-request and re-open the device with
  // no page reload. `wasOn: true` — the user just asked to turn it on. The other
  // outcomes (no device, in use, generic error) are not a permission block, so
  // they keep the toast.
  if (outcome === 'denied') {
    useDevicesStore.getState().beginPermissionRecovery(kind, true)
    return
  }
  notifyDeviceBlocked(kind, outcome)
}

/**
 * `viewOnly`, re-read at the moment we would actually turn something on.
 *
 * A permission prompt can sit open for seconds, and the server's verdict on a
 * passive join can land inside that window — so the check the toggles do before
 * asking is not the check that matters. `setViewOnly` forces both devices off;
 * without this a grant arriving afterwards would quietly undo it.
 */
const unlessViewOnly =
  (patch: Partial<MeetingSessionState>) =>
  (s: MeetingSessionState): Partial<MeetingSessionState> =>
    s.viewOnly ? {} : patch

export type RoomCredentials = {
  signalingWsUrl: string   // e.g. "wss://signal.instavc.com/socket"
  channelTopic: string     // e.g. "room:abc-123"
  roomToken: string        // Phoenix-signed JWT for WS auth
  simulcastEnabled: boolean
}

/** What a participant tile may do to the person it shows. */
export type ParticipantActions = {
  muteParticipant?: (id: string) => void
  requestAudio?: (id: string) => void
  stopVideo?: (id: string) => void
  requestVideo?: (id: string) => void
  stopScreenShare?: (id: string) => void
  removeParticipant?: (id: string) => void
  changeRole?: (id: string, role: ParticipantRole) => void
  /** Grant or revoke annotation access. */
  setAnnotator?: (id: string, granted: boolean) => void
}

type MeetingSessionState = {
  micOn: boolean
  /**
   * The microphone is muted OUTSIDE this app — the OS mic toggle (macOS Control
   * Center, Windows mic privacy), a headset's hardware button, or the OS pulling
   * the device — while the app still believes it is live (`micOn`, producer
   * resumed). So the person talks and the room hears nothing. Detected and set
   * by useLocalMedia (services/mic-interruption + mic-silence); read by
   * MicControl (the button warning) and useSystemMicMuteNotice (the toast).
   *
   * Advisory only: it changes nothing the room sees. Transient — never
   * persisted, and cleared on leave, since it describes right-now hardware.
   */
  systemMicMuted: boolean
  camOn: boolean
  /**
   * The sound of the screen share you are presenting reaches the room.
   *
   * Its own switch, on the stage — muting your microphone leaves it alone; a
   * moderator's mute turns it off. False while you share nothing or a share
   * without sound; set for each share as it starts. See services/share-audio.
   */
  shareAudioOn: boolean
  captionsOn: boolean
  handRaised: boolean
  isMyVideoPaused: boolean
  areIncomingVideosPaused: boolean
  /**
   * "Pause all incoming audio". Pauses the SFU consumer for every remote voice
   * (mic, H.323, SIP); share audio keeps playing. Reset on leave and on every
   * meeting entry (VcApp.enterMeetingWithJoinSound), unlike the video flag: a
   * meeting that opens silent looks broken rather than paused.
   */
  areIncomingAudiosPaused: boolean
  fullscreenOn: boolean
  /** Host-only "Lock room" toggle. The right-section in the control bar
   *  reads this to render a blinking lock badge while the room is locked. */
  roomLocked: boolean

  /** Set by GuestJoinPage / InstantMeetingDialog before navigating to
   *  /meeting/:id, or by VcApp's guest flow (?role=guest) on the Join click.
   *  Consumed by useMeetingConnection. */
  roomCredentials: RoomCredentials | null
  /**
   * View-only, decided by which link this person opened.
   *
   * Mirrors what the server already put in the room token; kept here so the
   * control bar can stop offering a camera the signaling server will refuse to
   * announce. Never the enforcement — flipping it grants nothing.
   */
  viewOnly: boolean
  /**
   * A shared video or embed is on stage AND playing sound.
   *
   * Read by the media layer to relax the microphone's speech-tuned processing
   * while the room is listening to something: RNNoise is a *speech* model, so
   * the playback every participant's mic picks up from their own speakers comes
   * back through it warbled, and RNNoise also switches the browser's own
   * echo canceller's partner filter off. Held here rather than passed down
   * because the media layer sits below the component that knows about shares.
   */
  mediaAudioPlaying: boolean
  /**
   * May the local user change other people — roles, mute, stop video?
   *
   * Computed once, from the same `hasPermission(role, 'role-modify')` the
   * Participants list uses, and read by anything that offers those actions. A
   * participant tile cannot work it out for itself: it is handed the person it
   * shows, never the person looking, and it renders from a dozen places that
   * would each have to be told. Holding one answer is what keeps the tile menu
   * and the Participants list from drifting apart.
   */
  canModerate: boolean
  /**
   * This viewer is the host. Only the host acts on the host, so a co-host's
   * tile menu offers nothing on the host's tile (the server refuses it too).
   */
  isRoomHost: boolean
  /**
   * Moderation actions the participant tiles invoke — the SAME functions the
   * Participants list is given, so the tile's three-dot menu does the thing
   * rather than something that resembles it.
   *
   * Held here because `VideoTile` renders from twenty-one places across the
   * grid, the rails, the pinned area and every share layout, and each would
   * otherwise have to pass all seven. None of them passed any: every moderation
   * item in that menu called an undefined prop through `?.()` and silently did
   * nothing.
   */
  participantActions: ParticipantActions
  /**
   * Is anyone besides the local user in the meeting?
   *
   * Published by MeetingScreen from the same count the layout uses, for the
   * same reason as `canModerate`: a tile only knows the person it shows. The
   * tile menu reads it to drop "Pin for all" when there is nobody else for it
   * to reach.
   */
  hasOtherParticipants: boolean
  /**
   * Peer ids of everyone presenting anything — a screen, the whiteboard, a
   * document or a video. Published by MeetingScreen for the tiles' "Presenting"
   * badge, for the same reason as `canModerate`: a tile only knows the person
   * it shows, not what is on the stage.
   */
  presentingIds: string[]

  setMediaAudioPlaying: (playing: boolean) => void
  setCanModerate: (can: boolean) => void
  setIsRoomHost: (isHost: boolean) => void
  setParticipantActions: (actions: ParticipantActions) => void
  setHasOtherParticipants: (has: boolean) => void
  setPresentingIds: (ids: string[]) => void
  toggleMic: () => void
  toggleCam: () => void
  /** Force mic/cam to a specific state. Used when a moderator stops your media
   *  — a toggle would race with whatever the local state happened to be. */
  setMicOn: (on: boolean) => void
  /** Set by the media layer when it detects (or clears) a system/hardware mute. */
  setSystemMicMuted: (muted: boolean) => void
  setCamOn: (on: boolean) => void
  setShareAudioOn: (on: boolean) => void
  toggleCaptions: () => void
  toggleHand: () => void
  /** Clear what belongs to a single visit to a meeting. */
  resetParticipationState: () => void
  /**
   * Mic and camera back to how a fresh page starts them — on sign-out, so the
   * last account's mute or camera-off does not follow the next person in.
   */
  resetMediaState: () => void
  toggleIncomingVideosPause: () => void
  toggleIncomingAudiosPause: () => void
  pauseMyVideo: () => void
  resumeMyVideo: () => void
  setFullscreenOn: (v: boolean) => void
  toggleRoomLocked: () => void
  setRoomLocked: (v: boolean) => void
  setRoomCredentials: (creds: RoomCredentials) => void
  clearRoomCredentials: () => void
  setViewOnly: (viewOnly: boolean) => void
}

export const useMeetingSessionStore = create<MeetingSessionState>((set, get) => ({
  // Both start off for a passive attendee: the devices are never opened, so a
  // control bar showing them "on" would be describing hardware that is not
  // running.
  micOn: !isPassiveJoinUrl(),
  systemMicMuted: false,
  camOn: !isPassiveJoinUrl(),
  shareAudioOn: false,
  captionsOn: false,
  handRaised: false,
  isMyVideoPaused: false,
  areIncomingVideosPaused: false,
  areIncomingAudiosPaused: false,
  fullscreenOn: false,
  roomLocked: false,
  roomCredentials: null,
  // Seeded from the URL at module load, not from an effect.
  //
  // The prejoin screen asks for the camera in its own mount effect, and React
  // runs child effects before parent ones — so a flag set by an effect in VcApp
  // arrives after the prompt it was meant to prevent. Evaluated here, it is
  // already true the first time anything reads it.
  viewOnly: isPassiveJoinUrl(),
  mediaAudioPlaying: false,
  canModerate: false,
  isRoomHost: false,
  participantActions: {},
  hasOtherParticipants: false,
  presentingIds: [],

  // A view-only attendee has nothing to toggle. Refused here rather than only
  // hidden in the control bar, because the same toggles are reachable from
  // keyboard shortcuts, the tile menu and the mobile bar — and the signaling
  // server refuses the producer regardless, which would leave the button lit
  // and the room hearing nothing.
  toggleMic: () => {
    if (get().viewOnly) return
    if (get().micOn) {
      set({ micOn: false })
      return
    }
    void whenAllowed('audio', () => set(unlessViewOnly({ micOn: true })))
  },
  setMicOn: (micOn) => {
    if (!micOn) {
      set({ micOn: false })
      return
    }
    // Gated for the same reason the toggle is: push-to-talk and the host's
    // "would you unmute?" prompt both come through here, and neither should be
    // able to light the button for a microphone the browser is refusing.
    void whenAllowed('audio', () => set(unlessViewOnly({ micOn: true })))
  },
  // Not gated by anything: it is an observation, not a request. The media layer
  // owns the one source of truth and this only mirrors it for the UI to read.
  setSystemMicMuted: (systemMicMuted) => set({ systemMicMuted }),
  // No browser permission to wait for: the sound came with the share's own
  // capture. The room's rule is checked by the caller (canShareAudio).
  setShareAudioOn: (shareAudioOn) =>
    set(shareAudioOn ? unlessViewOnly({ shareAudioOn: true }) : { shareAudioOn: false }),
  setCamOn: (camOn) => {
    if (!camOn) {
      set({ camOn: false })
      return
    }
    void whenAllowed('video', () => set(unlessViewOnly({ camOn: true, isMyVideoPaused: false })))
  },
  toggleCam: () => {
    if (get().viewOnly) return
    if (get().camOn) {
      set({ camOn: false })
      return
    }
    void whenAllowed('video', () => set(unlessViewOnly({ camOn: true, isMyVideoPaused: false })))
  },
  toggleCaptions: () => set((s) => ({ captionsOn: !s.captionsOn })),
  toggleHand: () => set((s) => ({ handRaised: !s.handRaised })),

  /**
   * Drop the state that belongs to one visit to a meeting.
   *
   * This store is module-scoped and never reset, so leaving a meeting and
   * rejoining without a page reload carried the last visit's flags into the
   * new one. A raised hand was the visible case: the hand stayed up locally
   * while the room knew nothing about it, because the mirror effect skips its
   * first run and then finds nothing changed — so the participant saw a hand
   * up that nobody else could see, and lowering it sent a `hand:lower` for a
   * hand that had never been raised.
   *
   * Deliberately NOT everything. `micOn` and `camOn` are choices the user made
   * in prejoin and expects to keep; `roomLocked` and `canModerate` are pushed
   * from the server on every join and correct themselves; `fullscreenOn`
   * mirrors the browser, and clearing the flag without leaving fullscreen would
   * only desynchronise it.
   */
  // `systemMicMuted` goes with the hand: it describes the hardware during one
  // visit, so a stale "your system mic is muted" must not greet the next room.
  resetParticipationState: () => set({ handRaised: false, systemMicMuted: false }),
  resetMediaState: () =>
    set({ micOn: !isPassiveJoinUrl(), camOn: !isPassiveJoinUrl(), isMyVideoPaused: false, systemMicMuted: false }),
  toggleIncomingVideosPause: () =>
    set((s) => ({ areIncomingVideosPaused: !s.areIncomingVideosPaused })),
  toggleIncomingAudiosPause: () => {
    const resuming = get().areIncomingAudiosPaused
    set({ areIncomingAudiosPaused: !resuming })
    // Unmuted here, inside the tap that called this, not only in the players'
    // effect: a phone browser pauses audio unmuted outside a gesture.
    if (resuming) unmuteRemoteVoicesNow()
  },
  pauseMyVideo: () => set({ isMyVideoPaused: true, camOn: false }),
  // Resuming re-opens the camera, so it asks like every other way of turning it
  // on — and stays PAUSED if the answer is no. Lifting the pause on a camera
  // that did not come back would leave the menu offering "Pause my video" over
  // a camera that is already off.
  resumeMyVideo: () =>
    void whenAllowed('video', () => set(unlessViewOnly({ isMyVideoPaused: false, camOn: true }))),
  setFullscreenOn: (fullscreenOn) => set({ fullscreenOn }),
  toggleRoomLocked: () => set((s) => ({ roomLocked: !s.roomLocked })),
  setRoomLocked: (roomLocked) => set({ roomLocked }),
  setRoomCredentials: (roomCredentials) => set({ roomCredentials }),
  // Leaving drops view-only with the credentials that carried it — the next
  // meeting decides for itself.
  //
  // The incoming-audio pause goes with them: the store outlives the meeting,
  // so a rejoin or the next call would otherwise open with everyone inaudible.
  // Breakout moves swap credentials without coming through here, so the pause
  // follows you into (and out of) a breakout room.
  clearRoomCredentials: () =>
    set({ roomCredentials: null, viewOnly: false, areIncomingAudiosPaused: false }),
  setMediaAudioPlaying: (mediaAudioPlaying) => set({ mediaAudioPlaying }),
  setCanModerate: (canModerate) => set({ canModerate }),
  setIsRoomHost: (isRoomHost) => set({ isRoomHost }),
  setParticipantActions: (participantActions) => set({ participantActions }),
  setHasOtherParticipants: (hasOtherParticipants) => set({ hasOtherParticipants }),
  setPresentingIds: (presentingIds) => set({ presentingIds }),
  // Turning it on closes the camera and microphone with it: the attendee may
  // have come through a prejoin screen with both armed, and leaving them on
  // would show them a live self-tile publishing to nobody.
  setViewOnly: (viewOnly) =>
    set(viewOnly ? { viewOnly, micOn: false, camOn: false } : { viewOnly }),
}))
